import { useEffect, useState, useRef } from "react";
import { Bell, Menu as MenuIcon, Network, Volume2, VolumeX, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { API } from "../../App";
import { useWebSocket } from "../../hooks/useWebSocket";
import { toast } from "sonner";

/**
 * Premium header for the Loja shell: store info pill, open/closed toggle, today's revenue
 * counter, sound toggle, alerts bell, network shortcut.
 */
export default function LojaHeader({ user, onMobileMenu, onOpenNetwork, badges = {} }) {
  const [merchant, setMerchant] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [todayOrders, setTodayOrders] = useState(0);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem("loja_sound_on") !== "0"; } catch { return true; }
  });
  const beepRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  // Load merchant info + today's stats
  useEffect(() => {
    (async () => {
      try {
        const h = { Authorization: `Bearer ${token}` };
        const [mRes, oRes] = await Promise.all([
          fetch(`${API}/merchants/me`, { headers: h }),
          fetch(`${API}/orders`, { headers: h }),
        ]);
        if (mRes.ok) {
          const m = await mRes.json();
          setMerchant(m);
          setIsOpen(m.is_open !== false);
        }
        if (oRes.ok) {
          const orders = await oRes.json();
          const today = new Date().toISOString().slice(0, 10);
          const todays = (orders || []).filter((o) => (o.created_at || "").slice(0, 10) === today);
          setTodayOrders(todays.length);
          setTodayRevenue(todays.filter((o) => o.status === "delivered").reduce((acc, o) => acc + Number(o.total || 0), 0));
        }
      } catch {}
    })();
  }, [token]);

  // Persist sound preference
  useEffect(() => {
    try { localStorage.setItem("loja_sound_on", soundOn ? "1" : "0"); } catch {}
  }, [soundOn]);

  // Ask for Notification permission once
  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
  }, []);

  // WebSocket: play sound + push notif on new order
  useWebSocket(token, {
    showToasts: false,
    onMessage: (msg) => {
      if (!msg) return;
      const t = msg.type || msg?.data?.type;
      if (t === "new_order" || t === "order_created") {
        // Update counters optimistically
        setTodayOrders((c) => c + 1);
        const orderTotal = Number(msg?.data?.total || msg?.total || 0);
        // Toast
        toast.success(`🛎️ Novo pedido — R$ ${orderTotal.toFixed(2).replace(".", ",")}`, { duration: 4500 });
        // Sound
        if (soundOn && beepRef.current) {
          try { beepRef.current.currentTime = 0; beepRef.current.play().catch(() => {}); } catch {}
        }
        // Vibrate
        if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate([120, 60, 120]);
        // Push
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          try { new Notification("GoFood Loja", { body: "Novo pedido recebido!", icon: "/logo192.png" }); } catch {}
        }
      }
    },
  });

  const toggleOpen = async () => {
    const next = !isOpen;
    setIsOpen(next);
    try {
      await fetch(`${API}/merchants/me`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_open: next }),
      });
      toast.success(next ? "Loja aberta — recebendo pedidos" : "Loja fechada");
    } catch {
      setIsOpen(!next);
      toast.error("Falha ao alternar status");
    }
  };

  const fmtBR = (n) => Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const initials = (merchant?.business_name || "L").slice(0, 2).toUpperCase();

  return (
    <header className="sticky top-0 z-20 bg-[#0B0D0F]/85 backdrop-blur-xl border-b border-white/5" data-testid="loja-header">
      {/* Beep audio (data URL) */}
      <audio ref={beepRef} preload="auto" data-testid="loja-beep">
        <source src="data:audio/wav;base64,UklGRtwAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YbgAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgIA=" type="audio/wav" />
      </audio>

      <div className="h-16 px-4 md:px-6 flex items-center gap-3">
        {/* Mobile menu trigger */}
        <button onClick={onMobileMenu} className="md:hidden w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center" data-testid="loja-mobile-trigger">
          <MenuIcon className="w-5 h-5" />
        </button>

        {/* Store pill */}
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00A86B] to-[#00D68F] flex items-center justify-center font-extrabold text-sm shrink-0 shadow-[0_0_12px_rgba(0,214,143,0.4)]" data-testid="loja-merchant-avatar">
            {initials}
          </div>
          <div className="min-w-0 hidden sm:block">
            <p className="text-xs font-extrabold leading-tight truncate" data-testid="loja-merchant-name">{merchant?.business_name || "Sua Loja"}</p>
            <button
              onClick={toggleOpen}
              className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${isOpen ? "text-[#00D68F]" : "text-[#FF4D4F]"}`}
              data-testid="loja-open-toggle"
            >
              <motion.span
                animate={isOpen ? { scale: [1, 1.3, 1] } : { scale: 1 }}
                transition={{ repeat: isOpen ? Infinity : 0, duration: 2 }}
                className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-[#00D68F] shadow-[0_0_6px_#00D68F]" : "bg-[#FF4D4F]"}`}
              />
              {isOpen ? "Aberta" : "Fechada"}
            </button>
          </div>
        </div>

        <div className="flex-1" />

        {/* Today revenue counter */}
        <div className="hidden lg:flex items-center gap-3 text-right border-r border-white/5 pr-4" data-testid="loja-header-today">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Faturamento hoje</p>
            <p className="text-lg font-extrabold text-[#00D68F] tabular-nums" data-testid="loja-header-revenue">
              R$ {fmtBR(todayRevenue)}
            </p>
          </div>
          <div className="border-l border-white/5 pl-3">
            <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Pedidos</p>
            <p className="text-lg font-extrabold tabular-nums" data-testid="loja-header-orders-today">
              {todayOrders}
            </p>
          </div>
        </div>

        {/* Sound toggle */}
        <button
          onClick={() => setSoundOn((s) => !s)}
          className="w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center relative"
          data-testid="loja-sound-toggle"
          aria-label={soundOn ? "Desligar som" : "Ligar som"}
          title={soundOn ? "Som ligado" : "Som desligado"}
        >
          {soundOn ? <Volume2 className="w-5 h-5 text-[#00D68F]" /> : <VolumeX className="w-5 h-5 text-white/40" />}
        </button>

        {/* Network */}
        <button
          onClick={onOpenNetwork}
          className="w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center"
          data-testid="loja-network-trigger"
          aria-label="Minha rede"
          title="Minha rede MLM"
        >
          <Network className="w-5 h-5 text-white/70" />
        </button>

        {/* Alerts bell */}
        <button
          className="w-10 h-10 rounded-lg hover:bg-white/5 flex items-center justify-center relative"
          data-testid="loja-bell"
          aria-label="Alertas"
        >
          <Bell className="w-5 h-5 text-white/70" />
          {badges.pendingOrdersCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF6B00] text-white text-[10px] font-extrabold flex items-center justify-center">
              {badges.pendingOrdersCount}
            </span>
          )}
        </button>
      </div>
    </header>
  );
}
